import cloneDeep from 'lodash/cloneDeep';
import mapValues from 'lodash/mapValues';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState, useSetRecoilState } from 'recoil';

import {
  chatSettingsInputsState,
  chatSettingsValueState,
  useAuth,
  useChatData,
  useChatInteract
} from '@chainlit/react-client';

import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

import { useChatSettingsSnapshotAtOpen } from '@/components/ChatSettings/useChatSettingsSnapshotAtOpen';
import type { TFormInputValue } from '@/components/ChatSettings/FormInput';

import {
  userSidebarOpenState,
  userSidebarSectionState
} from '@/state/project';

import AccountSection from './AccountSection';
import AdminSection from './AdminSection';
import CustomPromptManager from './CustomPromptManager';
import HelpSection from './HelpSection';
import { SECTION_META, SECTION_WIDGET_IDS } from './sectionConfig';
import UserDocsSection from './UserDocsSection';
import UserSidebarFooter from './UserSidebarFooter';
import UserSidebarHeader from './UserSidebarHeader';
import UserSidebarNav from './UserSidebarNav';
import WidgetSection from './WidgetSection';

export default function UserSidebar() {
  const { user, logout } = useAuth();
  const { chatSettingsValue, chatSettingsInputs } = useChatData();
  const { updateChatSettings, editChatSettings } = useChatInteract();
  const [sidebarOpen, setSidebarOpen] = useRecoilState(userSidebarOpenState);
  const [section, setSection] = useRecoilState(userSidebarSectionState);
  const setChatSettingsValue = useSetRecoilState(chatSettingsValueState);
  const setChatSettingsInputs = useSetRecoilState(chatSettingsInputsState);
  const { valuesAtOpen, inputsAtOpen } = useChatSettingsSnapshotAtOpen(
    sidebarOpen,
    chatSettingsValue,
    chatSettingsInputs
  );

  const { handleSubmit, setValue, reset, watch, getValues, formState } = useForm(
    {
      defaultValues: chatSettingsValue
    }
  );

  useEffect(() => {
    reset(chatSettingsValue);
  }, [chatSettingsValue, reset]);

  const restoreSnapshot = useCallback(() => {
    setChatSettingsInputs(cloneDeep(inputsAtOpen));
    setChatSettingsValue(cloneDeep(valuesAtOpen));
    reset(valuesAtOpen as Record<string, unknown>);
  }, [inputsAtOpen, valuesAtOpen, setChatSettingsInputs, setChatSettingsValue, reset]);

  const handleClose = useCallback(() => {
    restoreSnapshot();
    setSidebarOpen(false);
  }, [restoreSnapshot, setSidebarOpen]);

  const handleConfirm = handleSubmit((data) => {
    const processedValues = mapValues(data, (x: TFormInputValue) =>
      x !== '' ? x : null
    );
    updateChatSettings(processedValues);
    setChatSettingsValue(processedValues);
    setSidebarOpen(false);
  });

  const handleReset = () => {
    restoreSnapshot();
  };

  const setFieldValue = (field: string, value: any) => {
    setValue(field, value, { shouldDirty: true });
    editChatSettings(getValues());
  };

  const handleActiveBodyChange = useCallback(
    (body: string) => {
      setValue('custom_prompt', body, { shouldDirty: false });
      const merged = { ...getValues(), custom_prompt: body };
      const processed = mapValues(merged, (x: TFormInputValue) =>
        x !== '' ? x : null
      );
      updateChatSettings(processed);
      setChatSettingsValue(processed);
      reset(merged);
    },
    [setValue, getValues, updateChatSettings, setChatSettingsValue, reset]
  );

  const handleDocsFilterChange = useCallback(() => {
    const current = mapValues(getValues(), (x: TFormInputValue) =>
      x !== '' ? x : null
    );
    updateChatSettings(current);
    setChatSettingsValue(current);
  }, [getValues, updateChatSettings, setChatSettingsValue]);

  const values = watch();
  const sheetOpen = sidebarOpen;

  const userRole =
    (user?.metadata as Record<string, unknown> | undefined)?.role;
  const isAdmin = typeof userRole === 'string' && userRole.toLowerCase() === 'admin';

  const renderSection = () => {
    switch (section) {
      case 'account':
        return <AccountSection user={user} onLogout={() => logout(true)} />;
      case 'response':
      case 'features':
      case 'voice': {
        const meta = SECTION_META.find((m) => m.id === section);
        if (!meta) return null;
        return (
          <WidgetSection
            title={meta.label}
            blurb={meta.blurb}
            inputIds={SECTION_WIDGET_IDS[section]}
            allInputs={chatSettingsInputs as any[]}
            values={values}
            setField={setFieldValue}
          />
        );
      }
      case 'prompts':
        return <CustomPromptManager onActiveBodyChange={handleActiveBodyChange} />;
      case 'docs':
        return <UserDocsSection onFilterChange={handleDocsFilterChange} />;
      case 'help':
        return <HelpSection />;
      case 'admin':
        return isAdmin ? <AdminSection /> : null;
      default:
        return null;
    }
  };

  return (
    <Sheet open={sheetOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className={cn(
          'w-full p-0 flex flex-col gap-0 overflow-hidden transition-[max-width] duration-300 ease-out',
          section === 'admin' ? 'sm:max-w-[880px]' : 'sm:max-w-[520px]'
        )}
        aria-describedby={undefined}
      >
        <UserSidebarHeader user={user} />
        <div className="flex flex-col sm:flex-row flex-1 min-h-0">
          <UserSidebarNav section={section} onSelect={setSection} isAdmin={isAdmin} />
          <div className="flex-1 overflow-y-auto p-5">{renderSection()}</div>
        </div>
        <UserSidebarFooter
          dirty={formState.isDirty}
          onReset={handleReset}
          onCancel={handleClose}
          onSave={handleConfirm}
        />
      </SheetContent>
    </Sheet>
  );
}
